/**
 * サンプルコードの管理
 */
export interface SampleCode {
  id: string
  title: string
  description: string
  code: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  tags: string[]
}

export class SampleCodeManager {
  private static samples: SampleCode[] = [
    // 初級レベル
    {
      id: 'basic-for',
      title: '基本的なfor文',
      description: '0から9までの数字を出力する最も基本的なfor文の例です。',
      code: `// 基本的なfor文の例
int i;
for (i = 0; i < 10; i++) {
    // iの値が0から9まで順番に変化します
}`,
      difficulty: 'beginner',
      tags: ['for', '基本']
    },
    {
      id: 'sum-calculation',
      title: '合計値の計算',
      description: '0から4までの合計を計算するfor文の例です。',
      code: `// 0から4までの合計を計算
int sum = 0;
int i;

for (i = 0; i < 5; i++) {
    sum = sum + i;
}

// 最終的にsum = 10になります (0+1+2+3+4)`,
      difficulty: 'beginner',
      tags: ['for', '計算', '合計']
    },
    {
      id: 'basic-while',
      title: '基本的なwhile文',
      description: 'カウントダウンを行うwhile文の例です。',
      code: `// カウントダウンの例
int count = 5;

while (count > 0) {
    // countが5, 4, 3, 2, 1と減っていきます
    count = count - 1;
}`,
      difficulty: 'beginner',
      tags: ['while', '基本']
    },
    {
      id: 'basic-do-while',
      title: '基本的なdo-while文',
      description: '最低1回は実行されるdo-while文の例です。',
      code: `// do-while文の例
int num = 0;

do {
    num = num + 1;
    // 最低1回は実行されます
} while (num < 3);`,
      difficulty: 'beginner',
      tags: ['do-while', '基本']
    },
    
    // 中級レベル
    {
      id: 'factorial',
      title: '階乗の計算',
      description: '5の階乗（5!）を計算するfor文の例です。',
      code: `// 5の階乗を計算（5! = 5 × 4 × 3 × 2 × 1）
int factorial = 1;
int i;

for (i = 0; i < 5; i++) {
    factorial = factorial * (i + 1);
}

// 最終的にfactorial = 120になります`,
      difficulty: 'intermediate',
      tags: ['for', '計算', '階乗']
    },
    {
      id: 'nested-loops',
      title: 'ネストしたループ',
      description: '二重ループで九九の表を作成する例です。',
      code: `// 掛け算の表（3×3）
int i, j;
int result;

for (i = 0; i < 3; i++) {
    for (j = 0; j < 3; j++) {
        result = (i + 1) * (j + 1);
        // (i+1) × (j+1) の結果が計算されます
    }
}`,
      difficulty: 'intermediate',
      tags: ['for', 'ネスト', '九九']
    },
    {
      id: 'fibonacci',
      title: 'フィボナッチ数列',
      description: 'フィボナッチ数列の最初の10項を計算します。',
      code: `// フィボナッチ数列の計算
int a = 0, b = 1, c;
int i;

for (i = 0; i < 8; i++) {
    c = a + b;
    a = b;
    b = c;
}

// a=0, b=1, 1, 2, 3, 5, 8, 13, 21...`,
      difficulty: 'intermediate',
      tags: ['for', '数列', 'フィボナッチ']
    },
    
    // 上級レベル
    {
      id: 'prime-check',
      title: '素数判定',
      description: 'ある数が素数かどうかを判定するループです。',
      code: `// 7が素数かどうかを判定
int num = 7;
int isPrime = 1;  // 1:素数, 0:素数でない
int i;

for (i = 2; i < num; i++) {
    if (num % i == 0) {
        isPrime = 0;
        break;  // 割り切れたら素数ではない
    }
}

// isPrime = 1なら素数`,
      difficulty: 'advanced',
      tags: ['for', '判定', '素数', 'break']
    },
    {
      id: 'bubble-sort',
      title: 'バブルソート',
      description: '配列を昇順に並び替えるバブルソートの実装です。',
      code: `// バブルソート（簡略版）
int arr[5] = {5, 2, 8, 1, 9};
int i, j, temp;
int n = 5;

for (i = 0; i < n - 1; i++) {
    for (j = 0; j < n - i - 1; j++) {
        if (arr[j] > arr[j + 1]) {
            // 隣接要素を交換
            temp = arr[j];
            arr[j] = arr[j + 1];
            arr[j + 1] = temp;
        }
    }
}`,
      difficulty: 'advanced',
      tags: ['for', 'ネスト', 'ソート', '配列']
    },
    {
      id: 'pattern-triangle',
      title: '三角形パターン',
      description: 'ネストしたループで三角形のパターンを作成します。',
      code: `// 三角形パターンの作成
int i, j;
int rows = 5;

for (i = 0; i < rows; i++) {
    // 各行でi+1個の要素を処理
    for (j = 0; j <= i; j++) {
        // ここで出力処理を行う
    }
}

// 結果:
// *
// **
// ***
// ****
// *****`,
      difficulty: 'advanced',
      tags: ['for', 'ネスト', 'パターン']
    }
  ]
  
  /**
   * すべてのサンプルコードを取得
   */
  static getAllSamples(): SampleCode[] {
    return [...this.samples]
  }
  
  /**
   * 難易度別にサンプルを取得
   */
  static getSamplesByDifficulty(difficulty: 'beginner' | 'intermediate' | 'advanced'): SampleCode[] {
    return this.samples.filter(sample => sample.difficulty === difficulty)
  }
  
  /**
   * タグでサンプルを検索
   */
  static getSamplesByTag(tag: string): SampleCode[] {
    return this.samples.filter(sample => sample.tags.includes(tag))
  }
  
  /**
   * IDでサンプルを取得
   */
  static getSampleById(id: string): SampleCode | undefined {
    return this.samples.find(sample => sample.id === id)
  }
  
  /**
   * ランダムなサンプルを取得
   */
  static getRandomSample(difficulty?: 'beginner' | 'intermediate' | 'advanced'): SampleCode {
    const samples = difficulty ? this.getSamplesByDifficulty(difficulty) : this.samples
    return samples[Math.floor(Math.random() * samples.length)]
  }
}